import { Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormControl } from '@angular/forms';
import { SalesJobTypeService } from '../../services/sales-job-type.service';
import { SalesJobType } from '../../models/sales-type.model';
import { DynamicRouteHandlingService, GenericDropdownComponent } from '@zayan-erp/shared';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { rxResourceFixed } from '@zayan-erp/shared';
import { ToggleSwitch } from '@openng/optimus-ui/toggleswitch';
import { map, of, tap } from 'rxjs';
import { JobTypeService } from '../../services/job-type.service';
import { MajorCodeService } from '../../services/major-code.service';
import { MinorCodesService } from '../../services/minor-codes.service';

@Component({
  selector: 'lib-sales-job-type-form',
  imports: [GenericDropdownComponent, ToggleSwitch, FormsModule, ReactiveFormsModule],
  templateUrl: './sales-job-type-form.component.html'
})
export class SalesJobTypeFormComponent {
  #salesJobTypeService = inject(SalesJobTypeService);
  #dynamicRouteHandling = inject(DynamicRouteHandlingService);
  #activatedRoute = inject(ActivatedRoute);
  #jobTypeService = inject(JobTypeService);
  #majorCodeService = inject(MajorCodeService);
  #minorCodeService = inject(MinorCodesService);
  #router = inject(Router);
  #destroyRef = inject(DestroyRef);

  selectedSalesJobTypeId = signal<string | null>(this.#activatedRoute.snapshot.paramMap.get('salesJobTypeId'));
  selectedSalesTypeId = signal<string | null>(this.#activatedRoute.snapshot.paramMap.get('id'));
  selectedJobTypeId = signal<string | null>(null);
  selectedMajorCodeId = signal<string | null>(null);

  salesJobTypeForm = new FormGroup({
    salesTypeId: new FormControl<string | null>(this.selectedSalesTypeId()!, Validators.required),
    jobTypeId: new FormControl<string | null>(null, Validators.required),
    revenueMajorCodeId: new FormControl<string | null>(null, Validators.required),
    revenueMinorCodeId: new FormControl<string | null>(null, Validators.required),
    costMinorCodeId: new FormControl<string | null>(null, Validators.required),
    autoUpdate: new FormControl<boolean>(false, Validators.required)
  });

  jobTypeId = toSignal(this.salesJobTypeForm.get('jobTypeId')!.valueChanges);
  revenueMajorCodeId = toSignal(this.salesJobTypeForm.get('revenueMajorCodeId')!.valueChanges);
  jobTypeList = toSignal(
    this.#jobTypeService.readJobTypes().pipe(
      map(jobTypes =>
        jobTypes.map(jobType => ({
          id: jobType.id,
          firstLabel: jobType.code,
          secondLabel: jobType.description
        }))
      )
    )
  );

  revenueMajorCodeList = computed(() => this.revenueMajorCodeListRxResource.value() ?? []);
  minorCodeList = computed(() => this.minorCodeListRxResource.value() ?? []);

  constructor() {
    this.salesJobTypeForm
      .get('jobTypeId')
      ?.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.salesJobTypeForm.get('revenueMajorCodeId')?.setValue(null);
      });

    this.salesJobTypeForm
      .get('revenueMajorCodeId')
      ?.valueChanges.pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => {
        this.salesJobTypeForm.get('revenueMinorCodeId')?.setValue(null);
        this.salesJobTypeForm.get('costMinorCodeId')?.setValue(null);
      });

    this.#dynamicRouteHandling.submitButton$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
      this.submitSalesJobTypeForm();
    });
  }

  revenueMajorCodeListRxResource = rxResourceFixed({
    params: () => ({ jobTypeId: this.jobTypeId() }),
    defaultValue: null,
    stream: ({ params }) => {
      if (params.jobTypeId) {
        return this.#majorCodeService.readMajorCodes(params.jobTypeId ?? undefined).pipe(
          map(majorCodes =>
            majorCodes.map(majorCode => ({
              id: majorCode.id,
              firstLabel: majorCode.code,
              secondLabel: majorCode.description
            }))
          )
        );
      }
      return of(null);
    }
  });

  minorCodeListRxResource = rxResourceFixed({
    params: () => ({ majorCodeId: this.revenueMajorCodeId(), jobTypeId: this.jobTypeId() }),
    defaultValue: null,
    stream: ({ params }) => {
      if (params.majorCodeId && params.jobTypeId) {
        return this.#minorCodeService
          .readMinorCodes(params.majorCodeId ?? undefined, params.jobTypeId ?? undefined)
          .pipe(
            map(minorCodes =>
              minorCodes.map(minorCode => ({
                id: minorCode.id,
                firstLabel: minorCode.code,
                secondLabel: minorCode.description
              }))
            )
          );
      }
      return of(null);
    }
  });

  editRxResource = rxResourceFixed({
    params: () => ({ id: this.selectedSalesJobTypeId()! }),
    defaultValue: null,
    stream: ({ params }) => {
      if (params.id) {
        return this.#salesJobTypeService
          .getSalesJobTypeById(params.id)
          .pipe(tap(salesJobType => this.editForm(salesJobType)));
      }
      return of(null);
    }
  });

  editForm(salesJobType: SalesJobType) {
    this.salesJobTypeForm.patchValue({
      salesTypeId: salesJobType.salesTypeId,
      jobTypeId: salesJobType.jobTypeId,
      revenueMajorCodeId: salesJobType.revenueMajorCodeId,
      revenueMinorCodeId: salesJobType.revenueMinorCodeId,
      costMinorCodeId: salesJobType.costMinorCodeId,
      autoUpdate: salesJobType.autoUpdate
    });
  }

  submitSalesJobTypeForm() {
    if (this.salesJobTypeForm.invalid) {
      this.salesJobTypeForm.markAllAsTouched();
      return;
    }
    const salesJobType = {
      ...this.salesJobTypeForm.value,
      salesTypeId: this.selectedSalesTypeId()!
    };

    if (this.selectedSalesJobTypeId()) {
      this.#salesJobTypeService
        .updateSalesJobType(
          this.selectedSalesTypeId()!,
          this.selectedSalesJobTypeId()!,
          this.salesJobTypeForm.value as SalesJobType
        )
        .subscribe({
          next: () => this.afterSubmit()
        });
    } else {
      this.#salesJobTypeService
        .createSalesJobType(this.selectedSalesTypeId()!, salesJobType as SalesJobType)
        .subscribe({
          next: () => this.afterSubmit()
        });
    }
  }
  afterSubmit() {
    this.#dynamicRouteHandling.reload.next(!this.#dynamicRouteHandling.reload.value);
    this.#router.navigate([this.#activatedRoute.snapshot.data['returnRoute']], { queryParamsHandling: 'preserve' });
  }
}
